import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Radio, Users, Eye, MessageSquareQuote, Zap, Monitor, Smartphone, Tablet, Globe, TrendingUp, MapPinned } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const DEVICE_ICONS = { Mobile: Smartphone, Tablet: Tablet, Desktop: Monitor };

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-zinc-400 font-medium"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-3xl font-semibold mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function TableCard({ title, icon: Icon, rows, cols, empty = "No data yet for this range", testId }) {
  return (
    <div className="bg-white border rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-sm font-semibold mb-4">{Icon && <Icon className="w-4 h-4 text-zinc-400" />} {title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 py-4 text-center">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-wide text-zinc-400">
            {cols.map((c) => <th key={c.l} className={`pb-2 font-medium ${c.right ? "text-right" : ""}`}>{c.l}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-50">
                {cols.map((c) => <td key={c.l} className={`py-2 ${c.right ? "text-right tabular-nums" : ""} ${c.muted ? "text-zinc-500" : ""}`}>{c.get(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LiveNow() {
  const [live, setLive] = useState(null);
  const load = useCallback(() => api.get("/admin/analytics/live").then((r) => setLive(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="bg-white border rounded-xl p-5 mb-6" data-testid="an-live">
      <div className="flex items-center gap-2.5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <div className="text-sm font-semibold">Live now</div>
        <div className="text-2xl font-semibold tabular-nums ml-1" data-testid="an-live-count">{live ? live.count : "…"}</div>
        <div className="text-xs text-zinc-400">visitor{live && live.count === 1 ? "" : "s"} on the site · refreshes every 15s</div>
      </div>
      {live && live.visitors.length > 0 && (
        <div className="mt-4 divide-y divide-zinc-50">
          {live.visitors.map((v) => (
            <div key={v.session} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 text-sm" data-testid={`an-live-${v.session}`}>
              <span className="text-xs text-zinc-400 font-mono w-16">{v.session}</span>
              <span className="font-medium">{v.title || v.path}</span>
              <span className="text-zinc-400 text-xs">{v.path}</span>
              <span className="flex-1" />
              <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-50 border border-zinc-200 text-zinc-600">{v.channel}</span>
              <span className="text-xs text-zinc-400">{v.device}</span>
              <span className="text-xs text-zinc-400 tabular-nums w-16 text-right">{v.seconds_ago < 10 ? "just now" : `${v.seconds_ago}s ago`}</span>
            </div>
          ))}
        </div>
      )}
      {live && live.visitors.length === 0 && <div className="text-sm text-zinc-400 mt-3">Nobody is browsing right now.</div>}
    </div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api.get(`/admin/analytics/summary?days=${days}`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [days]);

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Site Analytics</h1>
          <p className="text-sm text-zinc-500 mt-1">First-party visitor tracking — works even when ad blockers hide GA4. Data kept for 90 days.</p>
        </div>
        <div className="flex gap-1 bg-zinc-100 rounded-lg p-1">
          {[7, 30, 90].map((n) => (
            <button key={n} type="button" onClick={() => setDays(n)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${days === n ? "bg-white shadow-sm font-medium" : "text-zinc-500 hover:text-zinc-800"}`}
              data-testid={`an-range-${n}`}>Last {n}d</button>
          ))}
        </div>
      </div>

      <LiveNow />

      {!d ? (
        <div className="text-sm text-zinc-400">Loading…</div>
      ) : d.error ? (
        <div className="text-sm text-red-500">Could not load analytics.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Users} label="Visitors" value={d.totals.visitors} sub={`${d.days}d · unique sessions`} testId="an-stat-visitors" />
            <StatCard icon={Eye} label="Pageviews" value={d.totals.pageviews} testId="an-stat-pageviews" />
            <StatCard icon={MessageSquareQuote} label="Enquiries" value={d.totals.enquiries} sub="leads captured" testId="an-stat-enquiries" />
            <StatCard icon={Zap} label="Engagement events" value={d.totals.events} sub="PDFs, calculators, clicks" testId="an-stat-events" />
          </div>

          <div className="bg-white border rounded-xl p-5 mb-6" data-testid="an-trend">
            <div className="flex items-center gap-2 text-sm font-semibold mb-4"><TrendingUp className="w-4 h-4 text-zinc-400" /> Daily trend</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#a1a1aa" }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#18181b" fill="#18181b" fillOpacity={0.08} strokeWidth={2} />
                  <Area type="monotone" dataKey="views" name="Pageviews" stroke="#9E7B4F" fill="#9E7B4F" fillOpacity={0.12} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <TableCard title="Traffic sources" icon={Globe} rows={d.sources} testId="an-sources"
              cols={[
                { l: "Channel", get: (r) => r.key },
                { l: "Visitors", right: true, get: (r) => r.visitors },
                { l: "Pageviews", right: true, muted: true, get: (r) => r.views },
              ]} />
            <TableCard title="Enquiries by source" icon={MessageSquareQuote} rows={d.lead_channels} testId="an-lead-sources"
              cols={[
                { l: "Channel", get: (r) => r.channel },
                { l: "Enquiries", right: true, get: (r) => r.count },
              ]} />
            <TableCard title="Top pages" icon={Eye} rows={d.top_pages} testId="an-top-pages"
              cols={[
                { l: "Page", get: (r) => <span className="font-mono text-xs">{r.key}</span> },
                { l: "Visitors", right: true, get: (r) => r.visitors },
                { l: "Views", right: true, muted: true, get: (r) => r.views },
              ]} />
            <TableCard title="Landing pages (first page of the visit)" icon={MapPinned} rows={d.landing_pages} testId="an-landing-pages"
              cols={[
                { l: "Page", get: (r) => <span className="font-mono text-xs">{r.key}</span> },
                { l: "Visits", right: true, get: (r) => r.sessions },
              ]} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <TableCard title="Devices" icon={Monitor} testId="an-devices"
              rows={d.devices}
              cols={[
                { l: "Device", get: (r) => { const I = DEVICE_ICONS[r.key] || Monitor; return <span className="inline-flex items-center gap-2"><I className="w-4 h-4 text-zinc-400" /> {r.key}</span>; } },
                { l: "Visitors", right: true, get: (r) => r.visitors },
              ]} />
            <TableCard title="Engagement events (with source)" icon={Zap} testId="an-conversions"
              rows={d.conversions}
              cols={[
                { l: "Event", get: (r) => <span className="font-medium">{r.event}</span> },
                { l: "Total", right: true, get: (r) => r.count },
                { l: "Top sources", get: (r) => <span className="text-xs text-zinc-500">{Object.entries(r.channels).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c, n]) => `${c} (${n})`).join(" · ")}</span> },
              ]} />
          </div>

          <p className="text-[11px] text-zinc-400 flex items-center gap-1.5" data-testid="an-note">
            <Radio className="w-3 h-3" /> Tip: add UTM parameters to your ad/social links (e.g. ?utm_source=instagram&utm_medium=social) and they'll show up here — plus in every lead.
          </p>
        </>
      )}
    </div>
  );
}
